import type { CompleteResponse } from "../api";
import { biomeForDate, courseNameForDate } from "@shared/biome";
import type { HoleLayout, ShotSample } from "@shared/physics";
import { mulberry32, seedFromString } from "@shared/rng";

export interface FeatureHole {
  holeIndex: number;
  layout: HoleLayout;
  samples: ShotSample[];
}

export interface SummaryInfo {
  courseDate: string;
  perHole: number[]; // length 9
  pars: number[]; // length 9
  totalStrokes: number;
  complete: CompleteResponse | null;
  handle: string;
  flag: string;
  countryCode: string;
  /** Practice-mode hooks; when present we swap the summary action row. */
  practice?: {
    onReshuffle: () => void;
    onExitToDaily: () => void;
  } | null;
  /** Best-performing hole: its layout + the winning-shot trajectory
   *  samples. Rendered as a mini trail snapshot inset on the share card. */
  featureHole?: FeatureHole | null;
}

export function showSummary(
  container: HTMLElement,
  info: SummaryInfo,
  onOpenLeaderboard: () => void,
): void {
  const sub = container.querySelector<HTMLElement>("#summary-sub")!;
  const holesEl = container.querySelector<HTMLElement>("#summary-holes")!;
  const totalEl = container.querySelector<HTMLElement>("#summary-total")!;
  const rankEl = container.querySelector<HTMLElement>("#summary-rank")!;
  const lbBtn = container.querySelector<HTMLButtonElement>(
    "#summary-leaderboard",
  )!;
  const shareBtn = container.querySelector<HTMLButtonElement>(
    "#summary-share",
  )!;
  const xBtn = container.querySelector<HTMLButtonElement>(
    "#summary-x",
  )!;
  const linkBtn = container.querySelector<HTMLButtonElement>(
    "#summary-link",
  )!;
  const copyBtn = container.querySelector<HTMLButtonElement>(
    "#summary-copy",
  )!;

  const totalPar = info.pars.reduce((a, b) => a + b, 0);
  const delta = info.totalStrokes - totalPar;
  const label = info.practice
    ? `Practice · Par ${totalPar} · ${deltaLabel(delta)}`
    : `${info.courseDate} · Par ${totalPar} · ${deltaLabel(delta)}`;
  sub.textContent = label;

  const cells = info.perHole.map((s, i) => {
    const par = info.pars[i] ?? 0;
    const d = s - par;
    const cls = d < 0 ? "under" : d > 0 ? "over" : "";
    return `<div class="hbox ${cls}">
      <div class="n">H${i + 1}</div>
      <div class="s">${s}</div>
    </div>`;
  });
  holesEl.innerHTML = cells.join("");

  totalEl.textContent = String(info.totalStrokes);
  if (info.complete?.ok) {
    const r = info.complete.rank;
    const n = info.complete.totalPlayers;
    rankEl.textContent = r ? `Rank #${r} of ${n}` : "Submitted";
  } else {
    rankEl.textContent = "";
  }

  const shareLabel = info.practice ? "♻️ New course" : "📸 Share card";
  const xLabel = info.practice ? "🏆 Play daily" : "𝕏 Post";
  const linkLabel = info.practice ? "" : "🔗 Copy link";
  const copyLabel = info.practice ? "" : "📋 Copy text";
  shareBtn.textContent = shareLabel;
  xBtn.textContent = xLabel;
  linkBtn.textContent = linkLabel;
  copyBtn.textContent = copyLabel;
  // In practice mode there's nothing to share (no permalink, no score
  // submission), so hide the secondary row entirely.
  const subRow = (copyBtn.parentElement as HTMLElement | null) ?? null;
  if (subRow) subRow.style.display = info.practice ? "none" : "";
  if (linkBtn) linkBtn.hidden = Boolean(info.practice);
  if (copyBtn) copyBtn.hidden = Boolean(info.practice);

  // Using .onclick (not addEventListener) so re-showing replaces the
  // previous handler instead of accumulating.
  if (info.practice) {
    const onReshuffle = info.practice.onReshuffle;
    shareBtn.onclick = () => {
      hide(container);
      onReshuffle();
    };
  } else {
    shareBtn.onclick = async () => {
      if (shareBtn.dataset.busy === "1") return;
      shareBtn.dataset.busy = "1";
      shareBtn.textContent = "Preparing…";
      try {
        const result = await shareScoreCard(info);
        shareBtn.textContent =
          result === "shared"
            ? "Shared ✓"
            : result === "copied"
              ? "Copied ✓"
              : result === "downloaded"
                ? "Saved ✓"
                : shareLabel;
      } catch {
        shareBtn.textContent = "Failed";
      } finally {
        setTimeout(() => {
          shareBtn.textContent = shareLabel;
          shareBtn.dataset.busy = "";
        }, 1600);
      }
    };
  }

  copyBtn.onclick = () => {
    copyScoreText(info);
    copyBtn.textContent = "Copied ✓";
    setTimeout(() => (copyBtn.textContent = copyLabel), 1400);
  };

  if (info.practice) {
    const onExit = info.practice.onExitToDaily;
    xBtn.onclick = () => {
      hide(container);
      onExit();
    };
  } else xBtn.onclick = async () => {
    // Open X intent immediately (inside the user gesture — otherwise popup
    // blockers kill it), then copy the PNG to the clipboard in parallel so
    // the user can paste it into the composer.
    const permalink = buildShareUrl(info.courseDate, info.handle);
    const text = buildXIntentText(info, permalink);
    const intent =
      `https://x.com/intent/post?text=${encodeURIComponent(text)}` +
      `&url=${encodeURIComponent(permalink)}`;
    window.open(intent, "_blank", "noopener,noreferrer");
    const copied = await copyCardPngToClipboard(info);
    xBtn.textContent = copied
      ? "Image copied — paste in composer"
      : "Opened ✓";
    setTimeout(() => (xBtn.textContent = xLabel), 2400);
  };

  linkBtn.onclick = async () => {
    const permalink = buildShareUrl(info.courseDate, info.handle);
    try {
      await navigator.clipboard.writeText(permalink);
      linkBtn.textContent = "Copied ✓";
    } catch {
      window.prompt("Copy your link:", permalink);
      linkBtn.textContent = "Copied ✓";
    }
    setTimeout(() => (linkBtn.textContent = linkLabel), 1400);
  };

  if (info.practice) {
    // No leaderboard for practice — repurpose the button so users aren't
    // stuck on the summary if they ignored the primary CTAs.
    lbBtn.textContent = "Close";
    lbBtn.onclick = () => hide(container);
  } else {
    lbBtn.textContent = "🏆 Leaderboard";
    lbBtn.onclick = () => onOpenLeaderboard();
  }

  container.hidden = false;
  requestAnimationFrame(() => container.classList.add("open"));
}

function hide(container: HTMLElement): void {
  container.classList.remove("open");
  setTimeout(() => {
    container.hidden = true;
  }, 320);
}

function copyScoreText(info: SummaryInfo): void {
  const totalPar = info.pars.reduce((a, b) => a + b, 0);
  const delta = info.totalStrokes - totalPar;
  const lines = [
    `Cosmic Par · ${info.courseDate}`,
    `${info.totalStrokes} strokes (${deltaLabel(delta)})`,
    info.perHole
      .map((s, i) => scoreEmoji(s - (info.pars[i] ?? 0)))
      .join(""),
    `cosmicpar.com`,
  ];
  const text = lines.join("\n");
  void navigator.clipboard?.writeText(text).catch(() => {
    window.prompt("Copy your score:", text);
  });
}

type ShareResult = "shared" | "copied" | "downloaded" | "cancelled";

async function shareScoreCard(info: SummaryInfo): Promise<ShareResult> {
  const canvas = buildScoreCardCanvas(info);
  const blob = await canvasToBlob(canvas);
  const file = new File([blob], `cosmic-par-${info.courseDate}.png`, {
    type: "image/png",
  });

  const totalPar = info.pars.reduce((a, b) => a + b, 0);
  const delta = info.totalStrokes - totalPar;
  const emojiRow = info.perHole
    .map((s, i) => scoreEmoji(s - (info.pars[i] ?? 0)))
    .join("");
  const shareText =
    `Cosmic Par · ${info.courseDate}\n` +
    `${info.totalStrokes} strokes (${deltaLabel(delta)})\n` +
    `${emojiRow}\n` +
    `https://cosmicpar.com`;

  // 1. Native share with file (mobile).
  const navAny = navigator as unknown as {
    canShare?: (data: { files?: File[] }) => boolean;
    share?: (data: {
      files?: File[];
      title?: string;
      text?: string;
    }) => Promise<void>;
  };
  if (
    typeof navAny.canShare === "function" &&
    typeof navAny.share === "function" &&
    navAny.canShare({ files: [file] })
  ) {
    try {
      await navAny.share({
        files: [file],
        title: "Cosmic Par",
        text: shareText,
      });
      return "shared";
    } catch (err) {
      if ((err as Error | null)?.name === "AbortError") return "cancelled";
      // Fall through to clipboard on other errors (permission denials, etc.)
    }
  }

  // 2. Clipboard image (desktop).
  const winAny = window as unknown as {
    ClipboardItem?: new (items: Record<string, Blob>) => unknown;
  };
  if (
    "clipboard" in navigator &&
    typeof navigator.clipboard.write === "function" &&
    typeof winAny.ClipboardItem === "function"
  ) {
    try {
      const ClipboardItemCtor = winAny.ClipboardItem;
      const item = new ClipboardItemCtor({
        "image/png": blob,
      }) as unknown as ClipboardItem;
      await navigator.clipboard.write([item]);
      return "copied";
    } catch {
      // Fall through to download.
    }
  }

  // 3. Download (last resort).
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cosmic-par-${info.courseDate}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return "downloaded";
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) resolve(b);
      else reject(new Error("canvas.toBlob failed"));
    }, "image/png");
  });
}

function buildScoreCardCanvas(info: SummaryInfo): HTMLCanvasElement {
  const W = 1080;
  const H = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  const theme = biomeForDate(info.courseDate);
  const courseName = courseNameForDate(info.courseDate);
  const totalPar = info.pars.reduce((a, b) => a + b, 0);
  const delta = info.totalStrokes - totalPar;

  const rgb = (c: [number, number, number]) =>
    `rgb(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(
      c[2] * 255,
    )})`;

  // Base nebula gradient.
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, rgb(theme.nebulaA));
  bg.addColorStop(0.55, rgb(theme.nebulaB));
  bg.addColorStop(1, rgb(theme.nebulaD));
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Warm highlight (feels like the sun/nebula core).
  const spot = ctx.createRadialGradient(
    W * 0.78,
    H * 0.18,
    0,
    W * 0.78,
    H * 0.18,
    W * 0.9,
  );
  spot.addColorStop(0, rgb(theme.nebulaC));
  spot.addColorStop(1, "rgba(0,0,0,0)");
  ctx.globalAlpha = 0.45;
  ctx.fillStyle = spot;
  ctx.fillRect(0, 0, W, H);
  ctx.globalAlpha = 1;

  // Stars — deterministic from date so the card for a given day is stable.
  const rng = mulberry32(seedFromString(`card/v1/${info.courseDate}`));
  for (let i = 0; i < 180; i++) {
    const x = rng() * W;
    const y = rng() * H;
    const r = rng() * 2.0 + 0.4;
    const pal = theme.starPalette;
    ctx.globalAlpha = 0.35 + rng() * 0.55;
    ctx.fillStyle = pal[Math.floor(rng() * pal.length)]!;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Dark inner card so text stays readable across biomes.
  const INSET = 56;
  const CX = INSET;
  const CY = INSET;
  const CW = W - INSET * 2;
  const CH = H - INSET * 2;
  roundRect(ctx, CX, CY, CW, CH, 40);
  ctx.fillStyle = "rgba(8, 10, 22, 0.78)";
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = hexWithAlpha(theme.accent, 0.38);
  ctx.stroke();

  const PAD = 56;
  const LEFT = CX + PAD;
  const RIGHT = CX + CW - PAD;

  // ---- Header block ----
  ctx.textBaseline = "top";
  ctx.textAlign = "left";

  ctx.fillStyle = "#ffffff";
  ctx.font = fontSpec(900, 68);
  ctx.fillText("COSMIC PAR", LEFT, CY + 60);

  ctx.fillStyle = theme.accent;
  ctx.font = fontSpec(600, 26);
  ctx.fillText(
    `${info.courseDate} · ${courseName}`,
    LEFT,
    CY + 60 + 82,
  );

  ctx.fillStyle = "#e8ecff";
  ctx.font = emojiFontSpec(700, 38);
  ctx.fillText(`${info.flag}  @${info.handle}`, LEFT, CY + 60 + 128);

  // ---- Score block ----
  const scoreY = CY + 290;

  // Tag above the big number.
  ctx.fillStyle = "rgba(232,236,255,0.55)";
  ctx.font = fontSpec(700, 18);
  ctx.fillText("TOTAL STROKES", LEFT, scoreY);

  // Big strokes number.
  ctx.fillStyle = theme.accent;
  ctx.font = fontSpec(900, 200);
  const numStr = String(info.totalStrokes);
  ctx.fillText(numStr, LEFT, scoreY + 26);
  const numW = ctx.measureText(numStr).width;

  // "strokes" label next to the big number.
  ctx.fillStyle = "rgba(232,236,255,0.65)";
  ctx.font = fontSpec(500, 32);
  ctx.fillText("strokes", LEFT + numW + 22, scoreY + 180);

  // Delta pill (colored).
  const deltaTxt = deltaLabel(delta);
  const deltaColor =
    delta < 0 ? "#9cff7c" : delta > 0 ? "#ff9e9e" : theme.accent;
  ctx.fillStyle = deltaColor;
  ctx.font = fontSpec(800, 42);
  ctx.fillText(deltaTxt, LEFT, scoreY + 230);

  // Rank (right of score block).
  if (info.complete?.ok && info.complete.rank) {
    const rank = info.complete.rank;
    const total = info.complete.totalPlayers;
    ctx.textAlign = "right";
    ctx.fillStyle = "rgba(232,236,255,0.55)";
    ctx.font = fontSpec(700, 18);
    ctx.fillText("RANK", RIGHT, scoreY);
    ctx.fillStyle = "#ffffff";
    ctx.font = fontSpec(900, 90);
    ctx.fillText(`#${rank}`, RIGHT, scoreY + 26);
    ctx.fillStyle = "rgba(232,236,255,0.55)";
    ctx.font = fontSpec(500, 22);
    ctx.fillText(`of ${total}`, RIGHT, scoreY + 122);
    ctx.textAlign = "left";
  }

  // ---- Best hole trail snapshot ----
  // Landscape Bird-Cam render of the player's winning shot on their
  // best hole. Placed between the score block and the per-hole grid.
  // Skipped when no feature hole is available (pre-computed resume
  // with no captured trajectory).
  const hasFeature = !!info.featureHole;
  const snapTop = scoreY + 270;
  const snapH = hasFeature ? 160 : 0;
  if (hasFeature) {
    drawTrailSnapshot(
      ctx,
      info.featureHole!,
      info.perHole[info.featureHole!.holeIndex] ?? 0,
      info.pars[info.featureHole!.holeIndex] ?? 0,
      theme,
      LEFT,
      snapTop,
      RIGHT - LEFT,
      snapH,
    );
  }

  // ---- Per-hole grid ----
  const gridTop = snapTop + snapH + (hasFeature ? 30 : 0);
  ctx.fillStyle = "rgba(232,236,255,0.55)";
  ctx.font = fontSpec(700, 18);
  ctx.fillText("PER HOLE", LEFT, gridTop);

  const holesY = gridTop + 34;
  const holesW = RIGHT - LEFT;
  const gap = 12;
  const cellW = (holesW - gap * 8) / 9;
  const cellH = hasFeature ? 120 : 160;
  for (let i = 0; i < 9; i++) {
    const x = LEFT + i * (cellW + gap);
    const par = info.pars[i] ?? 0;
    const s = info.perHole[i] ?? 0;
    const d = s - par;
    const col = d < 0 ? "#9cff7c" : d > 0 ? "#ff9e9e" : "#e8ecff";

    roundRect(ctx, x, holesY, cellW, cellH, 16);
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle =
      d < 0
        ? "rgba(156,255,124,0.25)"
        : d > 0
          ? "rgba(255,158,158,0.22)"
          : "rgba(255,255,255,0.08)";
    ctx.stroke();

    ctx.textAlign = "center";

    // Compact layout when the card also carries the trail snapshot.
    const labelOffY = hasFeature ? 12 : 16;
    const scoreOffY = hasFeature ? 34 : 46;
    const scorePx = hasFeature ? 42 : 54;
    const emojiOffY = hasFeature ? 82 : 110;
    const emojiPx = hasFeature ? 26 : 34;

    ctx.fillStyle = "rgba(232,236,255,0.5)";
    ctx.font = fontSpec(700, 18);
    ctx.fillText(`H${i + 1}`, x + cellW / 2, holesY + labelOffY);

    ctx.fillStyle = col;
    ctx.font = fontSpec(900, scorePx);
    ctx.fillText(String(s), x + cellW / 2, holesY + scoreOffY);

    ctx.font = emojiFontSpec(400, emojiPx);
    ctx.fillText(scoreEmoji(d), x + cellW / 2, holesY + emojiOffY);

    ctx.textAlign = "left";
  }

  // ---- Footer ----
  const footY = CY + CH - 66;
  ctx.fillStyle = hexWithAlpha(theme.accent, 0.9);
  ctx.font = fontSpec(800, 22);
  ctx.fillText("cosmicpar.com", LEFT, footY);

  ctx.fillStyle = "rgba(232,236,255,0.4)";
  ctx.font = fontSpec(500, 18);
  ctx.textAlign = "right";
  ctx.fillText("daily gravity golf · resets 13:37 UTC", RIGHT, footY + 2);
  ctx.textAlign = "left";

  return canvas;
}

// Mini Bird-Cam render of a played hole: arena outline, planets as
// filled circles, target as a pulsing ring, the trajectory as a bright
// stroke. Projection is xz → snapshot rect, with axes flipped so it
// reads "tee on the left, target on the right" even when the physical
// arena was rotated.
function drawTrailSnapshot(
  ctx: CanvasRenderingContext2D,
  feature: FeatureHole,
  strokes: number,
  par: number,
  theme: ReturnType<typeof biomeForDate>,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const { layout, samples, holeIndex } = feature;
  const delta = strokes - par;

  // Panel bg — subtle dark card that doesn't fight the main gradient.
  roundRect(ctx, x, y, w, h, 18);
  ctx.fillStyle = "rgba(255, 255, 255, 0.04)";
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = hexWithAlpha(theme.accent, 0.22);
  ctx.stroke();

  // Label row above the render area.
  const labelH = 36;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(232,236,255,0.55)";
  ctx.font = fontSpec(700, 16);
  ctx.fillText("BEST HOLE", x + 14, y + 10);
  ctx.fillStyle = "#ffffff";
  ctx.font = fontSpec(800, 18);
  ctx.fillText(`H${holeIndex + 1}`, x + 110, y + 10);

  // Score tag on the right of the label row.
  const scoreLabel =
    delta <= -2
      ? "EAGLE"
      : delta === -1
        ? "BIRDIE"
        : delta === 0
          ? "PAR"
          : delta === 1
            ? "BOGEY"
            : `+${delta}`;
  const scoreCol =
    delta < 0 ? "#9cff7c" : delta > 0 ? "#ff9e9e" : theme.accent;
  ctx.textAlign = "right";
  ctx.fillStyle = scoreCol;
  ctx.font = fontSpec(800, 18);
  ctx.fillText(`${strokes}/${par} · ${scoreLabel}`, x + w - 14, y + 10);
  ctx.textAlign = "left";

  // Render area: the full panel minus the label row and inset.
  const rx = x + 14;
  const ry = y + labelH;
  const rw = w - 28;
  const rh = h - labelH - 12;

  // Bounds of the arena in world units — use `bounds` from the layout
  // so elliptical + capsule-chain + wavy-river all fit naturally.
  const minX = layout.bounds.minX;
  const maxX = layout.bounds.maxX;
  const minZ = layout.bounds.minZ;
  const maxZ = layout.bounds.maxZ;
  const worldW = maxX - minX || 1;
  const worldH = maxZ - minZ || 1;
  // Fit with 6% padding.
  const pad = 0.06;
  const scale = Math.min(
    (rw * (1 - pad * 2)) / worldW,
    (rh * (1 - pad * 2)) / worldH,
  );
  const centerX = rx + rw / 2;
  const centerY = ry + rh / 2;
  const worldCX = (minX + maxX) / 2;
  const worldCZ = (minZ + maxZ) / 2;
  const proj = (wx: number, wz: number): [number, number] => [
    centerX + (wx - worldCX) * scale,
    centerY + (wz - worldCZ) * scale,
  ];

  // Arena outline — ellipse if capsule-free, else capsule-chain union.
  ctx.save();
  ctx.strokeStyle = hexWithAlpha(theme.accent, 0.55);
  ctx.lineWidth = 1.5;
  if (layout.arenaSegments && layout.arenaSegments.length > 0) {
    // Approximate the union outline by drawing each capsule as a stadium
    // shape (rectangle + two end caps). Good-enough for a snapshot.
    for (const seg of layout.arenaSegments) {
      const [ax, ay] = proj(seg.ax, seg.az);
      const [bx, by] = proj(seg.bx, seg.bz);
      const segR = seg.radius * scale;
      const dx = bx - ax;
      const dy = by - ay;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      const angle = Math.atan2(dy, dx);
      ctx.beginPath();
      ctx.moveTo(ax + nx * segR, ay + ny * segR);
      ctx.lineTo(bx + nx * segR, by + ny * segR);
      ctx.arc(bx, by, segR, angle + Math.PI / 2, angle - Math.PI / 2, true);
      ctx.lineTo(ax - nx * segR, ay - ny * segR);
      ctx.arc(ax, ay, segR, angle - Math.PI / 2, angle + Math.PI / 2, true);
      ctx.closePath();
      ctx.stroke();
    }
  } else {
    const rX = (layout.arenaRadiusX ?? layout.arenaRadius ?? 0) * scale;
    const rZ = (layout.arenaRadiusZ ?? layout.arenaRadius ?? 0) * scale;
    const [cx, cy] = proj(0, 0);
    ctx.beginPath();
    ctx.ellipse(cx, cy, rX, rZ, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();

  // Planets — filled circles tinted by the biome accent.
  for (const p of layout.planets) {
    const [px, py] = proj(p.cx, p.cz);
    const pr = Math.max(2, p.radius * scale);
    ctx.beginPath();
    ctx.arc(px, py, pr, 0, Math.PI * 2);
    // Repulsor (negative strength) draws in a cool tint to contrast.
    ctx.fillStyle = p.strength < 0
      ? "rgba(120, 200, 255, 0.55)"
      : hexWithAlpha(theme.accent, 0.55);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.18)";
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Target — bright ring at its position.
  const [tx, ty] = proj(layout.target.cx, layout.target.cz);
  const tr = Math.max(3, layout.target.radius * scale);
  ctx.beginPath();
  ctx.arc(tx, ty, tr, 0, Math.PI * 2);
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(tx, ty, Math.max(1.5, tr * 0.4), 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  // Tee dot — faint outline.
  const [teeX, teeY] = proj(layout.tee.x, layout.tee.z);
  ctx.beginPath();
  ctx.arc(teeX, teeY, 3.5, 0, Math.PI * 2);
  ctx.fillStyle = hexWithAlpha(theme.accent, 0.9);
  ctx.fill();

  // Trajectory — the winning line. Wormhole teleports show as jumps in
  // the samples, so split-draw on any sample-to-sample distance that
  // exceeds ~8 world units to avoid a straight line across the arena.
  ctx.save();
  ctx.strokeStyle = "#ffd9a8";
  ctx.lineWidth = 2.2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.shadowColor = hexWithAlpha(theme.accent, 0.6);
  ctx.shadowBlur = 6;
  const JUMP = 8;
  let penDown = false;
  ctx.beginPath();
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]!;
    const [sx, sy] = proj(s.x, s.z);
    if (i === 0) {
      ctx.moveTo(sx, sy);
      penDown = true;
      continue;
    }
    const prev = samples[i - 1]!;
    const dx = s.x - prev.x;
    const dz = s.z - prev.z;
    if (Math.hypot(dx, dz) > JUMP) {
      // Wormhole / out-of-bounds reset — lift the pen and restart.
      if (penDown) ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(sx, sy);
    } else {
      ctx.lineTo(sx, sy);
    }
  }
  if (penDown) ctx.stroke();
  ctx.restore();
}

function fontSpec(weight: number, size: number): string {
  return `${weight} ${size}px ui-sans-serif, -apple-system, "SF Pro Display", "SF Pro Text", "Segoe UI", Inter, system-ui, sans-serif`;
}

function emojiFontSpec(weight: number, size: number): string {
  return `${weight} ${size}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", ui-sans-serif, -apple-system, "SF Pro Text", system-ui, sans-serif`;
}

function hexWithAlpha(hex: string, alpha: number): string {
  // #RRGGBB → rgba(r,g,b,alpha).
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

function deltaLabel(d: number): string {
  if (d === 0) return "Even par";
  if (d < 0) return `${Math.abs(d)} under`;
  return `+${d} over`;
}

function scoreEmoji(d: number): string {
  if (d <= -2) return "🦅"; // eagle
  if (d === -1) return "🐦"; // birdie
  if (d === 0) return "🟢"; // par
  if (d === 1) return "🟡"; // bogey
  return "🔴";
}

function buildShareUrl(courseDate: string, handle: string): string {
  const origin =
    typeof location !== "undefined" && location.origin
      ? location.origin
      : "https://cosmicpar.com";
  return `${origin}/s/${encodeURIComponent(courseDate)}/${encodeURIComponent(
    handle,
  )}`;
}

function buildXIntentText(info: SummaryInfo, _permalink: string): string {
  const totalPar = info.pars.reduce((a, b) => a + b, 0);
  const delta = info.totalStrokes - totalPar;
  const emojiRow = info.perHole
    .map((s, i) => scoreEmoji(s - (info.pars[i] ?? 0)))
    .join("");
  // X appends the URL separately via &url=...; we just carry the body.
  return (
    `Cosmic Par · ${info.courseDate}\n` +
    `${info.totalStrokes} strokes (${deltaLabel(delta)})\n` +
    `${emojiRow}`
  );
}

async function copyCardPngToClipboard(info: SummaryInfo): Promise<boolean> {
  const winAny = window as unknown as {
    ClipboardItem?: new (items: Record<string, Blob>) => unknown;
  };
  if (
    !("clipboard" in navigator) ||
    typeof navigator.clipboard.write !== "function" ||
    typeof winAny.ClipboardItem !== "function"
  ) {
    return false;
  }
  try {
    const canvas = buildScoreCardCanvas(info);
    const blob = await canvasToBlob(canvas);
    const ClipboardItemCtor = winAny.ClipboardItem;
    const item = new ClipboardItemCtor({
      "image/png": blob,
    }) as unknown as ClipboardItem;
    await navigator.clipboard.write([item]);
    return true;
  } catch {
    return false;
  }
}
